import { cn } from '../../shared/lib/cn';
import { StatusBadge } from '../../shared/ui/status-badge';
import { errorClassMeta, statusMeta } from '../../shared/status/dictionary';
import { FACET_VALUE_WORD, NAMED_FACETS, type TFacetField } from '../../entities/event';

export interface IFacetValue {
  value: string | null;
  label?: string | null;
  deleted?: boolean;
}

// A missing `label` means the thing has no name: show the id rather than inventing one.
export const facetValueText = (field: TFacetField, bucket: IFacetValue): string => {
  const value = bucket.value ?? '';
  if (NAMED_FACETS.has(field)) {
    return !bucket.deleted && bucket.label ? bucket.label : value;
  }
  if (field === 'status') return statusMeta('event', value)?.label ?? value;
  // The dictionary must come first in this chain, or an issue and an event print different words.
  if (field === 'errorClass')
    return errorClassMeta(value)?.label ?? FACET_VALUE_WORD[field]?.[value] ?? value;
  return FACET_VALUE_WORD[field]?.[value] ?? value;
};

const isRawId = (field: TFacetField, bucket: IFacetValue) =>
  NAMED_FACETS.has(field) && (bucket.deleted === true || !bucket.label);

export const FacetValue = ({
  field,
  bucket,
  className,
}: {
  field: TFacetField;
  bucket: IFacetValue;
  className?: string;
}) => {
  if (field === 'status') return <StatusBadge vocabulary="event" value={bucket.value ?? ''} />;

  const text = facetValueText(field, bucket);
  const raw = isRawId(field, bucket);

  return (
    <span className={cn('flex min-w-0 items-baseline gap-2', className)}>
      <span
        className={cn('truncate', raw && 'font-mono')}
        title={raw ? (bucket.deleted ? 'This entity was deleted' : 'No name on record') : text}
      >
        {text || '—'}
      </span>
      {bucket.deleted && <span className="shrink-0 text-micro text-faint">deleted</span>}
    </span>
  );
};
